import React from 'react';

interface ProductionMetadataValues {
  dropboxVideoUrl: string;
  videoFilename: string;
  dropboxDrpUrl: string;
  timelineName: string;
  localVideoPath: string;
  productionNotes: string;
  autoPrepareVideoSource?: boolean;
}

interface ProductionMetadataFieldsProps {
  values: ProductionMetadataValues;
  onChange: (updates: Partial<ProductionMetadataValues>) => void;
  isDarkMode: boolean;
  showAutoPrepareToggle?: boolean;
  compact?: boolean;
}

const ProductionMetadataFields: React.FC<ProductionMetadataFieldsProps> = ({
  values,
  onChange,
  isDarkMode,
  showAutoPrepareToggle = false,
  compact = false,
}) => {
  const inputClassName = `w-full px-3 py-2 rounded-lg border text-sm ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  } focus:ring-2 focus:ring-primary-500 focus:border-primary-500`;

  const stackClassName = compact ? 'space-y-3' : 'space-y-4';
  const textAreaClassName = `${inputClassName} min-h-[104px] resize-y`;

  return (
    <div className={stackClassName}>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Dropbox Video URL
        </label>
        <input
          type="url"
          value={values.dropboxVideoUrl}
          onChange={(e) => onChange({ dropboxVideoUrl: e.target.value })}
          placeholder="https://www.dropbox.com/..."
          className={inputClassName}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Local Video Path
        </label>
        <input
          type="text"
          value={values.localVideoPath}
          onChange={(e) => onChange({ localVideoPath: e.target.value })}
          placeholder="/Users/.../video.mov"
          className={inputClassName}
        />
        <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Stored on the session and your user mapping for OTIO/export workflows.
        </p>
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Timeline Name
        </label>
        <input
          type="text"
          value={values.timelineName}
          onChange={(e) => onChange({ timelineName: e.target.value })}
          placeholder="e.g., Main Edit v2"
          className={inputClassName}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          DRP Dropbox URL
        </label>
        <input
          type="url"
          value={values.dropboxDrpUrl}
          onChange={(e) => onChange({ dropboxDrpUrl: e.target.value })}
          placeholder="https://www.dropbox.com/..."
          className={inputClassName}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Video Filename
        </label>
        <input
          type="text"
          value={values.videoFilename}
          onChange={(e) => onChange({ videoFilename: e.target.value })}
          placeholder="e.g., Anna Patreon.mov"
          className={inputClassName}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Production Notes
        </label>
        <textarea
          value={values.productionNotes}
          onChange={(e) => onChange({ productionNotes: e.target.value })}
          placeholder="e.g., RX denoise, de-ess, loudness normalized to -16 LUFS"
          className={textAreaClassName}
        />
        <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Free-form notes for audio cleanup, mastering, captions, delivery, or other production decisions.
        </p>
      </div>

      {showAutoPrepareToggle && (
        <label className={`flex items-start gap-3 rounded-lg border px-3 py-3 ${
          isDarkMode ? 'border-gray-700 bg-gray-800/70 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-700'
        }`}>
          <input
            type="checkbox"
            checked={values.autoPrepareVideoSource !== false}
            onChange={(e) => onChange({ autoPrepareVideoSource: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-sm">
            Queue AWS source ingest and proxy generation after the transcript session is created.
          </span>
        </label>
      )}
    </div>
  );
};

export default ProductionMetadataFields;
